import { refreshTokensIfNeeded } from "@/api/service"
import { getTokenFromStorage, isTokenExpired } from "@/helpers/jwt"
import {
  IBattleAutoToggledData,
  IBattleFinishedData,
  IBattleRoundResolvedData
} from "@/store/apis/battleAPI/battle.types"
import { createSocketBase } from "@/store/apis/socket.factory"
import { SOCKET_EVENTS } from "@/store/apis/socketRegistry"

const SERVER_CLOSE = "io server disconnect"

export interface IBattleSocketHandlers {
  onRoundResolved: (data: IBattleRoundResolvedData) => void
  onAutoToggled: (data: IBattleAutoToggledData) => void
  onFinished: (data: IBattleFinishedData) => void
}

export const battleSocket = {
  ...createSocketBase("battle"),

  reinitGuard: false,
  reconnectCallbacks: new Set<() => void>(),

  start(onReconnect: () => void): () => void {
    this.reconnectCallbacks.add(onReconnect)
    this._open()
    return () => {
      this.reconnectCallbacks.delete(onReconnect)
    }
  },

  _open() {
    let connectedBefore = false
    const rereadAll = (): void => {
      this.reconnectCallbacks.forEach((callback) => callback())
    }
    this._init({
      auth: () => ({ token: getTokenFromStorage("accessToken") ?? "" }),
      onConnect: () => {
        this.reinitGuard = false
        if (!connectedBefore) {
          connectedBefore = true
          rereadAll()
        }
      },
      onReconnect: rereadAll,
      onConnectError: () => {
        const token = getTokenFromStorage("accessToken")
        if (token && isTokenExpired(token) && !this.reinitGuard) {
          this.reinitGuard = true
          this._refreshAndReopen()
        }
      },
      onDisconnect: (reason) => {
        if (reason === SERVER_CLOSE) {
          this._refreshAndReopen()
        }
      }
    })
  },

  _refreshAndReopen() {
    void refreshTokensIfNeeded().then((ok) => {
      if (ok && this._subs > 0) {
        this._reset()
        this._open()
      }
    })
  },

  bindBattleEvents(battleId: string, handlers: IBattleSocketHandlers) {
    const unsubs = [
      this._bind(SOCKET_EVENTS.battleRoundResolved, (e) => {
        if (e.data.battleId === battleId) handlers.onRoundResolved(e.data)
      }),
      this._bind(SOCKET_EVENTS.battleAutoToggled, (e) => {
        if (e.data.battleId === battleId) handlers.onAutoToggled(e.data)
      }),
      this._bind(SOCKET_EVENTS.battleFinished, (e) => {
        if (e.data.battleId === battleId) handlers.onFinished(e.data)
      })
    ]
    return () => unsubs.forEach((u) => u())
  }
}
